import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { formatApiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";
import { Users, MessageSquare, Building2, Video, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("Please enter your work email and password."); return; }
    setBusy(true);
    try {
      await login(email, password);
      nav("/app/office");
    } catch (e2) {
      setErr(formatApiErr(e2.response?.data?.detail) || "Sign in failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white" data-testid="login-page">
      {/* Left brand pane */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden text-white"
           style={{ background: "linear-gradient(160deg,#111114 0%,#1a1a1e 55%,#241a17 100%)" }}>
        <div className="absolute -top-20 -right-24 w-96 h-96 rounded-full opacity-25 blur-3xl" style={{ background: "#D95D39" }}/>
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ background: "#F08A5D" }}/>
        <div className="relative z-10"><BrandMark theme="dark"/></div>
        <div className="relative z-10 max-w-lg">
          <h1 className="text-5xl font-semibold leading-[1.05] mb-6 tracking-tight">
            Work together,<br/>
            <span className="text-[var(--brand-accent)]">wherever.</span>
          </h1>
          <p className="text-zinc-300 text-base leading-relaxed mb-10">
            TOVRA brings your team&apos;s communication, presence, and workspace into one calm
            place — so collaboration flows even when the office doesn&apos;t.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <Feature icon={Building2} title="Virtual office" body="See who&apos;s in — in real time."/>
            <Feature icon={MessageSquare} title="Channels & DMs" body="Threaded, searchable, calm."/>
            <Feature icon={Users} title="Presence" body="Available, focus, away — clear."/>
            <Feature icon={Video} title="Huddles" body="One-tap voice and video."/>
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-500 tracking-widest">
          POWERED BY TOVRA
        </div>
      </div>

      {/* Right sign-in pane */}
      <div className="flex items-center justify-center p-8 bg-[var(--bg-app)]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form" aria-label="Sign in">
          <div className="lg:hidden mb-6"><BrandMark theme="light"/></div>
          <h2 className="text-3xl font-semibold mb-1">Sign in</h2>
          <p className="text-sm text-zinc-500 mb-8">Welcome back to your workspace.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500">Work email</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" required className="h-11 mt-1"/>
            </div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
                <Link to="/forgot" className="text-xs text-zinc-500 hover:text-zinc-900">Forgot?</Link>
              </div>
              <Input id="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? "text" : "password"} autoComplete="current-password" required className="h-11 mt-1 pr-11"/>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                data-testid="login-toggle-password"
                className="absolute right-3 top-[38px] p-1 text-zinc-400 hover:text-zinc-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
            {err && <div data-testid="login-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
            <Button data-testid="login-submit" disabled={busy} type="submit" className="w-full h-11 bg-[var(--brand-primary)] hover:bg-black text-white font-medium">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="text-sm mt-6 text-zinc-500 text-center">
            New to your team&apos;s TOVRA? <Link to="/register" className="text-[var(--brand-accent)] font-medium">Create account →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[var(--brand-accent)]"/>
      </div>
      <div>
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="text-xs text-zinc-400 mt-0.5">{body}</div>
      </div>
    </div>
  );
}
